import Grid from "@mui/material/Grid/Grid";
import TextField from "@mui/material/TextField/TextField";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import * as Yup from 'yup';
import Form, { FormDirty, FormErrors, FormFooterAllButtons, FormValidationSchema } from "../../../../components/FormControl/Form/Form";
import Panel from "../../../../components/Panel/Panel";
import PanelBodyFormContent from "../../../../components/Panel/PanelBodyFormContent";
import SelectSearch from "../../../../components/SelectSearch";
import OptionItem from "../../../../components/SelectSearch/OptionItem";
import SelectSearchMultiAsync from "../../../../components/SelectSearch/SelectSearchMultiAsync";
import TicketLayoutType from "../../../../model/TicketLayoutType";
import TicketType from "../../../../model/TicketType";
import { ServiceApi, WhereCondition } from "../../../../service/ServiceApi";
import TicketTypeApi from "../../../../service/TicketTypeApi";

const api = new TicketTypeApi();

export enum TicketLayoutTypeRule {
    Optional = 0,
    NotVisible = 1,
    Required = 2
}

export type TicketLayoutTypeFormData = {
    name: string;
    rule_tag: TicketLayoutTypeRule;
    rule_final_customer: TicketLayoutTypeRule;
    rule_require_attachment_development: TicketLayoutTypeRule;
    rule_origin: TicketLayoutTypeRule;
    rule_client_contacts: TicketLayoutTypeRule;
    rule_post_service_tab: TicketLayoutTypeRule;
    rule_effort_estimate: TicketLayoutTypeRule;
    rule_delivery_forecast: TicketLayoutTypeRule;
    rule_quantity_to_be_produced: TicketLayoutTypeRule;
    default_priority: number | null;
    ticket_status_ids: number[];
    ticket_status_ids_options: OptionItem<number>[];
    rule_last_status_update: TicketLayoutTypeRule;
    rule_attachment_client: TicketLayoutTypeRule;
    rule_attachment_analysis: TicketLayoutTypeRule;
    rule_attachment_solution: TicketLayoutTypeRule;
    rule_checklist: TicketLayoutTypeRule;
    rule_comment_attachment: TicketLayoutTypeRule;
    rule_comment_reminder: TicketLayoutTypeRule;
}

export interface TicketLayoutTypeFormProps {
    title: React.ReactNode;
    item?: TicketLayoutType;
    onSubmit: (data: TicketLayoutTypeFormData, isDirty?: boolean, submitType?: string | null) => Promise<any>;
    onCancel: () => any;
    onChange?: (changed: boolean) => any;
    onUndo?: () => any;
}

export default function TicketTypeForm(props: TicketLayoutTypeFormProps) {

    const { title, item, onSubmit, onCancel, onChange, onUndo } = props;
    const [isDirty, setDirty] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();

    const [ticketStatusList, setTicketStatusList] = useState<OptionItem<number>[]>([]);
    const apiTicketStatus = new ServiceApi<TicketType>('ticketStatus');

    useEffect(() => {
        onSearchTicketStatus('').then(setTicketStatusList);
    }, []);

    async function onSearchTicketStatus(search: string, selectedTicketStatus?: number[]) {
        const where: WhereCondition<TicketType> = [['name', 'ilike', `%${search}%`], ['use_in_task_internal', '=', true]];
        if (selectedTicketStatus && selectedTicketStatus.length > 0) {
            where.push(['id', 'notIn', selectedTicketStatus]);
        }
        const ticketStatuses = await apiTicketStatus.getList({ where, limit: 100, order: { name: 'ASC' } });
        if (selectedTicketStatus && selectedTicketStatus.length > 0) {
            const usersAux = await apiTicketStatus.getList({ where: [['id', 'in', selectedTicketStatus]] });
            ticketStatuses.unshift(...usersAux);
        }
        return ticketStatuses.map((u) => {
            return { value: u.id, label: u.name };
        });
    }

    const initialValues: TicketLayoutTypeFormData = {
        name: item?.name || '',
        ticket_status_ids: item?.tickets_status?.map(u => u.id) || [],
        ticket_status_ids_options: item?.tickets_status?.map(u => ({ value: u.id, label: u.name })) || [],
        rule_tag: item?.rule_tag || TicketLayoutTypeRule.Optional,
        rule_final_customer: item?.rule_final_customer || TicketLayoutTypeRule.Optional,
        rule_require_attachment_development: item?.rule_require_attachment_development || TicketLayoutTypeRule.Optional,
        rule_origin: item?.rule_origin || TicketLayoutTypeRule.Optional,
        rule_client_contacts: item?.rule_client_contacts || TicketLayoutTypeRule.Optional,
        rule_post_service_tab: item?.rule_post_service_tab || TicketLayoutTypeRule.Optional,
        rule_effort_estimate: item?.rule_effort_estimate || TicketLayoutTypeRule.Optional,
        rule_delivery_forecast: item?.rule_delivery_forecast || TicketLayoutTypeRule.Optional,
        rule_quantity_to_be_produced: item?.rule_quantity_to_be_produced || TicketLayoutTypeRule.Optional,
        default_priority: item?.default_priority || null,
        rule_last_status_update: item?.rule_last_status_update || TicketLayoutTypeRule.Optional,
        rule_attachment_client: item?.rule_attachment_client || TicketLayoutTypeRule.Optional,
        rule_attachment_analysis: item?.rule_attachment_analysis || TicketLayoutTypeRule.Optional,
        rule_attachment_solution: item?.rule_attachment_solution || TicketLayoutTypeRule.Optional,
        rule_checklist: item?.rule_checklist || TicketLayoutTypeRule.Optional,
        rule_comment_attachment: item?.rule_comment_attachment || TicketLayoutTypeRule.Optional,
        rule_comment_reminder: item?.rule_comment_reminder || TicketLayoutTypeRule.Optional
    };

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<TicketLayoutTypeFormData> = {
            name: Yup.string()
                .trim()
                .required()
                .min(3)
                .max(80)
                .label('Nome'),
            default_priority: Yup.number()
                .nullable()
                .min(1)
                .max(999)
                .label('Prioridade padrão'),
        }

        return validationSchema;
    }

    async function onSubmitHandler(values: TicketLayoutTypeFormData, dirty: FormDirty<TicketLayoutTypeFormData>, errors?: FormErrors<TicketLayoutTypeFormData>, submitType?: string | null) {
        const isDirty = Object.values(dirty).reduce((p, c) => p || c, false);

        if (errors) {
            for (var error of Object.values(errors).filter(e => !!e)) {
                enqueueSnackbar(error, { variant: 'error' });
            }
            return;
        }

        await onSubmit(values, isDirty, submitType);
    }

    function setChanges(changed: boolean) {
        setDirty(changed);
        if (onChange) onChange(changed);
    }

    async function validateName(name: string): Promise<string | undefined> {
        const where: WhereCondition<TicketLayoutType> = [
            ['name', '=', name]
        ];

        if (item?.id) {
            where.push(['id', '!=', item.id]);
        }

        const count = await api.count({ where });
        if (count > 0) {
            return 'Este nome já está cadastrado';
        }
    }

    async function postValidation(values: TicketLayoutTypeFormData): Promise<FormErrors<TicketLayoutTypeFormData>> {
        return {
            name: await validateName(values.name)
        };
    }

    return (
        <Form<TicketLayoutTypeFormData>
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            onSubmit={onSubmitHandler}
            onChange={setChanges}
            postValidation={postValidation}
            style={{ overflow: 'auto' }}
        >
            {({ isSubmitting, values, errors, handleChange, handleChangeNumber, dirty, setFieldValue }) => {

                return (
                    <Panel
                        panelHeaderTitle={title}
                        panelFooterButtons={<FormFooterAllButtons onUndo={onUndo} onBack={onCancel} isDirty={isDirty} />}

                    >
                        <PanelBodyFormContent style={{ overflow: 'auto' }}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item sm={12} md={6} lg={6} >
                                    <TextField
                                        name="name"
                                        label='Nome'
                                        margin="dense"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        autoFocus={true}
                                        value={values.name}
                                        onChange={handleChange}
                                        error={!!errors.name && !!dirty.name}
                                        helperText={!!errors.name && !!dirty.name && errors.name}
                                    />
                                    <TextField
                                        name="default_priority"
                                        label={'Prioridade padrão'}
                                        margin="dense"
                                        variant="outlined"
                                        fullWidth
                                        inputProps={{ maxLength: 3 }}
                                        value={values.default_priority || ''}
                                        onChange={e => {
                                            const re = /^[0-9\b]+$/;
                                            if (e.target.value === '' || re.test(e.target.value)) {
                                                handleChangeNumber(e);
                                            }
                                        }}
                                        error={!!errors.default_priority && !!dirty.default_priority}
                                        helperText={!!errors.default_priority && !!dirty.default_priority && errors.default_priority}
                                    />
                                    <SelectSearchMultiAsync<number>
                                        placeholder="Status visíveis nesse tipo de layout"
                                        label="Status visíveis nesse tipo de layout"
                                        loadOptions={(s, ss) => onSearchTicketStatus(s, ss?.map(as => as.value) || values.ticket_status_ids)}
                                        value={values.ticket_status_ids_options || []}
                                        onChangeHandle={async (_, value) => {
                                            await setFieldValue('ticket_status_ids_options', value);
                                            await setFieldValue('ticket_status_ids', value.map(v => v.value));
                                        }}
                                        name='ticket_status_ids'
                                        defaultOptions={ticketStatusList}
                                        fullWidth
                                        margin='dense'
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_tag'
                                        label='Regra para tags?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_tag}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_tag', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_final_customer'
                                        label='Regra para cliente final?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_final_customer}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_final_customer', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_require_attachment_development'
                                        label='Regra para anexos de desenvolvimento?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                            // { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_require_attachment_development}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_require_attachment_development', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_origin'
                                        label='Regra para origem?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_origin}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_origin', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_client_contacts'
                                        label='Regra para contatos do cliente?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_client_contacts}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_client_contacts', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_post_service_tab'
                                        label='Regra para aba pós-atendimento?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                            // { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_post_service_tab}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_post_service_tab', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_effort_estimate'
                                        label='Regra para estimativa de esforço?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_effort_estimate}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_effort_estimate', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_delivery_forecast'
                                        label='Regra para previsão de entrega?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_delivery_forecast}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_delivery_forecast', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_quantity_to_be_produced'
                                        label='Regra para quantidade a ser produzida?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Opcional' },
                                            { value: TicketLayoutTypeRule.Required, label: 'Obrigatório' }
                                        ]}
                                        value={values.rule_quantity_to_be_produced}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_quantity_to_be_produced', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_last_status_update'
                                        label='Regra para última atualização de status?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_last_status_update}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_last_status_update', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_attachment_client'
                                        label='Regra para anexos da solicitação?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_attachment_client}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_attachment_client', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_attachment_analysis'
                                        label='Regra para anexos durante o desenvolvimento?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_attachment_analysis}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_attachment_analysis', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_attachment_solution'
                                        label='Regra para anexos da resolução?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_attachment_solution}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_attachment_solution', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_checklist'
                                        label='Regra para checklists?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_checklist}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_checklist', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_comment_attachment'
                                        label='Regra para anexos nos comentários?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_comment_attachment}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_comment_attachment', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                    <SelectSearch<TicketLayoutTypeRule>
                                        name='rule_comment_reminder'
                                        label='Regra para adição de lembrete automática dentro do comentário?'
                                        options={[
                                            { value: TicketLayoutTypeRule.NotVisible, label: 'Não visível' },
                                            { value: TicketLayoutTypeRule.Optional, label: 'Visível' }
                                        ]}
                                        value={values.rule_comment_reminder}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('rule_comment_reminder', v || 0);
                                        }}
                                        margin='dense'
                                        fullWidth
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </PanelBodyFormContent>
                    </Panel>
                );
            }}
        </Form>
    )
}